import { useCallback, useEffect, useState } from 'react'
import {
  ServiceFeeDetailsRecord,
  StaffUpdateServiceFeeInfoForCampaignRequest,
  StaffUpdateServiceFeeInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useStaffUpdateServiceFeeInfoForCampaign,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRow,
  AgxTypography,
  Icon_FeaturedIcon,
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import EditServiceFeeReason, { ChangeDetails } from './EditServiceFeeReason'
import { useSelectedCampaign } from 'hooks'

enum EditServiceFeeModalPage {
  Reason = 'Reason',
  Details = 'Details',
  UpdateSuccess = 'UpdateSuccess',
}

const EditServiceFeeModal = ({
  onClose,
  currentServiceFeeDetails,
}: {
  onClose: () => void
  currentServiceFeeDetails: ServiceFeeDetailsRecord
}) => {
  const [currentPage, setCurrentPage] = useState<EditServiceFeeModalPage>(
    EditServiceFeeModalPage.Reason
  )

  const [reasonDetails, setReasonDetails] = useState<ChangeDetails | undefined>(
    undefined
  )
  const [serviceFeeDetails, setServiceFeeDetails] =
    useState<ServiceFeeDetailsRecord>(() => currentServiceFeeDetails)

  const [reasonDetailsValid, setReasonDetailsValid] = useState(false)

  useEffect(() => {
    if (!reasonDetails) setReasonDetailsValid(false)
    else if (
      reasonDetails?.reasonType ===
        StaffUpdateServiceFeeInfoForCampaignRequestChangeType.Other &&
      !reasonDetails?.reason
    )
      setReasonDetailsValid(false)
    else setReasonDetailsValid(true)
  }, [reasonDetails?.reason, reasonDetails?.reasonType, setReasonDetailsValid])

  const invalidateServiceFeeInfo =
    useInvalidateUseStaffGetCommissionInfoForCampaign()

  const { campaign: selectedCampaign } = useSelectedCampaign()

  const {
    staffUpdateServiceFeeInfoForCampaign,
    staffUpdateServiceFeeInfoForCampaignPending,
  } = useStaffUpdateServiceFeeInfoForCampaign({
    onSuccess: async () => {
      await invalidateServiceFeeInfo(selectedCampaign?.campaignId || '')
      setCurrentPage(EditServiceFeeModalPage.UpdateSuccess)
    },
    onError: (error) => {
      console.error('Error updating ServiceFee info:', error)
    },
  })

  useEffect(() => {
    if (currentPage === EditServiceFeeModalPage.UpdateSuccess) {
      const timer = setTimeout(() => {
        onClose()
        setCurrentPage(EditServiceFeeModalPage.Reason)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentPage])

  const onReasonDetailsUpdated = useCallback(
    (updatedDetails: ChangeDetails) => {
      setReasonDetails(updatedDetails)
    },
    [setReasonDetails]
  )

  return (
    <AgxModal
      type="single"
      minWidth="800px"
      maxWidth="800px"
      title={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? ''
          : currentPage === EditServiceFeeModalPage.Reason
            ? 'Provide reason for Service Fee update'
            : 'Edit Service Fee'
      }
      variant={
        currentPage === EditServiceFeeModalPage.UpdateSuccess
          ? 'withoutDivider'
          : 'withDivider'
      }
      actionButtonContainerStyle={'withoutDivider'}
      content={
        <AgxRow
          gap="none"
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          {currentPage === EditServiceFeeModalPage.Reason && (
            <EditServiceFeeReason
              defaultChangeDetails={reasonDetails}
              onChange={onReasonDetailsUpdated}
            />
          )}
          {currentPage === EditServiceFeeModalPage.Details && (
            <AgxTextInput
              id={'ServiceFeePercantage'}
              onInputValueChange={({ value }: { value: string }) =>
                setServiceFeeDetails((prev) => {
                  const updated = Object.create(Object.getPrototypeOf(prev))
                  Object.assign(updated, prev, {
                    serviceFeePercentage: value,
                  })
                  return updated
                })
              }
              type="percent"
              hideOptionalText
              defaultValue={serviceFeeDetails.serviceFeePercentage?.toString()}
              required
              errorText={
                serviceFeeDetails.serviceFeePercentage == null ||
                serviceFeeDetails.serviceFeePercentage <= 0
                  ? 'Enter service fee'
                  : undefined
              }
            />
          )}
          {currentPage === EditServiceFeeModalPage.UpdateSuccess && (
            <AgxColumn
              gap={'spacing-lg'}
              alignItems="alignCenter"
              justifyContent="center"
              fill
            >
              <Icon_FeaturedIcon />
              <AgxTypography
                id="ServiceFeeUpdateSuccessMessage"
                type="body_ui_strong"
                size="medium"
                color="var(--text-primary)"
              >
                ServiceFee updated successfully!
              </AgxTypography>
            </AgxColumn>
          )}
        </AgxRow>
      }
      setIsDialogOpen={onClose}
      actionButtons={
        <>
          {currentPage === EditServiceFeeModalPage.Reason && (
            <AgxRow gap="spacing-lg" fill justifyContent="center">
              <AgxButton
                id="ServiceFeeReasonCancel"
                text="Cancel"
                variant="hollow"
                onClick={onClose}
                size="large"
                stretch
              />
              <AgxButton
                id="ServiceFeeReasonNext"
                text="Next"
                variant="primary"
                onClick={() => setCurrentPage(EditServiceFeeModalPage.Details)}
                disabled={!reasonDetailsValid}
                size="large"
                stretch
              />
            </AgxRow>
          )}
          {currentPage === EditServiceFeeModalPage.Details && (
            <AgxRow gap="spacing-lg" fill justifyContent="center">
              <AgxButton
                id="ServiceFeeDetailsBack"
                text="Back"
                variant="hollow"
                onClick={() => setCurrentPage(EditServiceFeeModalPage.Reason)}
                size="large"
                stretch
                disabled={staffUpdateServiceFeeInfoForCampaignPending}
              />
              <AgxButton
                id="ServiceFeeDetailsSave"
                text="Save"
                variant="primary"
                onClick={async () => {
                  if (!serviceFeeDetails || !reasonDetails?.reasonType) {
                    return
                  }
                  await staffUpdateServiceFeeInfoForCampaign({
                    campaignId: selectedCampaign?.campaignId || '',
                    serviceFeePercentage:
                      serviceFeeDetails.serviceFeePercentage,
                    changeType: reasonDetails.reasonType,
                    reason: reasonDetails.reason,
                  } as StaffUpdateServiceFeeInfoForCampaignRequest)
                }}
                disabled={
                  !serviceFeeDetails ||
                  !reasonDetailsValid ||
                  staffUpdateServiceFeeInfoForCampaignPending
                }
                size="large"
                stretch
              />
            </AgxRow>
          )}
        </>
      }
    />
  )
}

export default EditServiceFeeModal
